/* src/sections/auth/festival/themes/RamnavmiTheme.js */
import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Divine & Royal Color Palette
const COLORS = {
    SAFFRON_MAIN: '#FF7722', // Deep Saffron (Bhagwa)
    GOLD_SHIMMER: '#FFD700', // Royal Gold
    WARM_RED: '#D32F2F',     // For petals/accents
    DIVINE_LIGHT: '#FFF8E1', // Almost white gold light
    BG_DARK_WARM: '#1A0A00', // Very dark warm brown/black
};

// Ram Navami Canvas Component (Animation Engine)
function RamNavamiCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;
            const petals = [];
            const divineRays = [];
            let sunAura = null;
            let textParticles = [];

            // Set canvas size
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // --- 1. Floating Petals Factory (Pushp Varsha) ---
            const createPetal = () => {
                const colors = [COLORS.SAFFRON_MAIN, COLORS.GOLD_SHIMMER, COLORS.WARM_RED];
                return {
                    x: Math.random() * canvas.width,
                    y: -20,
                    size: Math.random() * 5 + 3,
                    speedX: Math.random() * 1 - 0.5,
                    speedY: Math.random() * 1 + 0.5,
                    rotation: Math.random() * Math.PI * 2,
                    rotationSpeed: (Math.random() - 0.5) * 0.02,
                    color: colors[Math.floor(Math.random() * colors.length)],
                    opacity: Math.random() * 0.5 + 0.3,
                };
            };

            const updatePetal = (p) => {
                p.x += p.speedX;
                p.y += p.speedY;
                p.rotation += p.rotationSpeed;
                if (p.y > canvas.height + 20) {
                    p.y = -20;
                    p.x = Math.random() * canvas.width;
                }
            };

            const drawPetal = (p) => {
                ctx.save();
                ctx.globalAlpha = p.opacity;
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);
                ctx.fillStyle = p.color;
                ctx.beginPath();
                ctx.ellipse(0, 0, p.size, p.size * 0.6, 0, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            };

            // --- 2. Divine Rays Factory (Rising Energy/Arrows) ---
            const createDivineRay = () => ({
                x: Math.random() * canvas.width,
                y: canvas.height + Math.random() * 200,
                width: Math.random() * 2 + 1,
                height: Math.random() * 40 + 20,
                speedY: Math.random() * 1.5 + 0.8,
                opacity: Math.random() * 0.3 + 0.1,
            });

            const updateDivineRay = (r) => {
                r.y -= r.speedY;
                if (r.y + r.height < 0) {
                    r.y = canvas.height + 100;
                    r.x = Math.random() * canvas.width;
                }
            };

            const drawDivineRay = (r) => {
                ctx.save();
                ctx.globalAlpha = r.opacity;
                const gradient = ctx.createLinearGradient(r.x, r.y, r.x, r.y + r.height);
                gradient.addColorStop(0, COLORS.GOLD_SHIMMER);
                gradient.addColorStop(1, 'rgba(255, 215, 0, 0)');

                ctx.fillStyle = gradient;
                ctx.shadowBlur = 10;
                ctx.shadowColor = COLORS.GOLD_SHIMMER;
                ctx.beginPath();
                ctx.rect(r.x, r.y, r.width, r.height);
                ctx.fill();
                ctx.restore();
            };

            // --- 3. Background Sun Aura Factory (Suryavansha Symbol) ---
            const createSunAura = () => ({
                x: canvas.width / 2,
                y: canvas.height * 0.3,
                size: Math.max(canvas.width, canvas.height) * 0.4,
                rotation: 0,
            });

            const updateSunAura = (s) => {
                s.rotation += 0.002;
            };

            const drawSunAura = (s) => {
                ctx.save();
                ctx.translate(s.x, s.y);
                ctx.rotate(s.rotation);
                ctx.globalAlpha = 0.08;
                ctx.strokeStyle = COLORS.GOLD_SHIMMER;
                ctx.lineWidth = 2;

                for (let i = 0; i < 12; i += 1) {
                    ctx.rotate(Math.PI / 6);
                    ctx.beginPath();
                    ctx.moveTo(s.size * 0.5, 0);
                    ctx.lineTo(s.size, 0);
                    ctx.stroke();
                }
                ctx.beginPath(); ctx.arc(0, 0, s.size * 0.6, 0, Math.PI * 2); ctx.stroke();
                ctx.beginPath(); ctx.arc(0, 0, s.size * 0.8, 0, Math.PI * 2); ctx.stroke();

                ctx.restore();
            };

            // --- 4. Text Particle Factory ---
            const createTextParticle = (x, y, char, index) => ({
                x,
                y: y - 80,
                targetY: y,
                char,
                delay: index * 12,
                time: 0,
                alpha: 0,
                scale: 0,
            });

            const updateTextParticle = (t) => {
                t.time += 1;
                if (t.time > t.delay) {
                    if (t.y < t.targetY) t.y += 8;
                    t.alpha = Math.min(1, t.alpha + 0.1);
                    t.scale = Math.min(1, t.scale + 0.1);
                }
            };

            const drawTextParticle = (t) => {
                if (t.time < t.delay) return;
                ctx.save();
                ctx.globalAlpha = t.alpha;
                ctx.translate(t.x, t.y);
                ctx.scale(t.scale, t.scale);
                ctx.font = 'bold 54px "serif", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                ctx.shadowBlur = 20;
                ctx.shadowColor = COLORS.GOLD_SHIMMER;

                const gradient = ctx.createLinearGradient(0, -25, 0, 25);
                gradient.addColorStop(0, COLORS.GOLD_SHIMMER);
                gradient.addColorStop(1, COLORS.SAFFRON_MAIN);
                ctx.fillStyle = gradient;

                ctx.fillText(t.char, 0, 0);

                ctx.strokeStyle = COLORS.DIVINE_LIGHT;
                ctx.lineWidth = 0.5;
                ctx.strokeText(t.char, 0, 0);
                ctx.restore();
            };

            // --- Initialization ---
            for (let i = 0; i < 70; i += 1) petals.push(createPetal());
            for (let i = 0; i < 40; i += 1) divineRays.push(createDivineRay());
            sunAura = createSunAura();

            // Create Text: JAI SHRI RAM
            const createText = () => {
                const text = 'JAI SHRI RAM';
                const isDesktop = canvas.width >= 900;
                const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
                const centerY = canvas.height * 0.15;
                const charSpacing = canvas.width < 768 ? 30 : 45;
                const startX = centerX - (text.length * charSpacing) / 2;
                textParticles = [];
                for (let i = 0; i < text.length; i += 1) {
                    const currentSpacing = text[i] === ' ' ? charSpacing / 2 : charSpacing;
                    textParticles.push(createTextParticle(startX + i * currentSpacing, centerY, text[i], i));
                }
            };
            setTimeout(createText, 50);

            // --- Animation Loop ---
            const animate = () => {
                ctx.fillStyle = `rgba(26, 10, 0, 0.25)`;
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Draw Background Aura
                if (sunAura) {
                    updateSunAura(sunAura);
                    drawSunAura(sunAura);
                }

                // Draw Rising Divine Rays
                divineRays.forEach(r => { updateDivineRay(r); drawDivineRay(r); });

                // Draw Falling Petals
                petals.forEach(p => { updatePetal(p); drawPetal(p); });

                // Draw Text
                textParticles.forEach(t => { updateTextParticle(t); drawTextParticle(t); });

                animationFrameId = requestAnimationFrame(animate);
            };
            animate();

            // Handle Resize
            const handleResize = () => {
                resizeCanvas();
                if (textParticles.length > 0) createText();
            };
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                pointerEvents: 'none', zIndex: 2,
            }}
        />
    );
}

// Main Theme Export
export default function RamNavamiTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            background: `radial-gradient(circle at top center, #4a2c00 0%, ${COLORS.BG_DARK_WARM} 80%)`,
            overflow: 'hidden',
        }}>
            <RamNavamiCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(15,5,0,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha(COLORS.GOLD_SHIMMER, 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.45)}`,
                    `0 16px 64px ${alpha(COLORS.SAFFRON_MAIN, 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: `linear-gradient(90deg, transparent, ${COLORS.SAFFRON_MAIN}, ${COLORS.GOLD_SHIMMER}, ${COLORS.WARM_RED}, transparent)`,
                    borderRadius: '0 0 4px 4px',
                    animation: 'ramGlow 3s ease-in-out infinite',
                },
                '@keyframes ramGlow': { '0%, 100%': { opacity: 0.6 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: `linear-gradient(90deg, ${COLORS.SAFFRON_MAIN}, ${COLORS.GOLD_SHIMMER}, ${COLORS.WARM_RED}, ${COLORS.SAFFRON_MAIN})`,
                        backgroundSize: '300% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha(COLORS.SAFFRON_MAIN, 0.5)}`,
                    }}>🏹 &nbsp; Jai Shri Ram &nbsp; 🏹</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2, position: 'relative', zIndex: 2 }}><Logo /></Stack>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    {cloneElement(children, { hideHelpLink: true })}
                </Box>
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha(COLORS.SAFFRON_MAIN, 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

RamNavamiTheme.propTypes = {
    children: PropTypes.node,
};
